import { EngagementFeedBucket, EngagementRedFlag } from '@/__generated__/graphql';

/**
 * ENG-5684 — wire shape → staff feed rows.
 *
 * The prototype's `components/FulfillmentPassiveFeed/derive.ts` is NOT reused.
 * It derives from `EngagementScene`, and `viewModel.ts` documents why that
 * adapter cannot exist; its filter and sort logic also moved server-side, since
 * buckets are an api enum and ordering is an api ORDER BY over a cursor. What
 * is left here is projection and the display fallbacks.
 */

/**
 * The api's own enum, aliased rather than re-declared as a string union. The
 * two vocabularies were minted together against the prototype's `FilterBucket`,
 * and taking the generated type is what keeps that true: a bucket added or
 * renamed server-side fails the label map below at compile time instead of
 * silently rendering a pill that filters nothing.
 *
 * `closed` covers canceled, because to an operator both are simply over.
 */
export type StaffFeedBucket = EngagementFeedBucket;

export const STAFF_FILTER_LABELS: Record<StaffFeedBucket, string> = {
  [EngagementFeedBucket.All]: 'All',
  [EngagementFeedBucket.Pending]: 'Pending',
  [EngagementFeedBucket.Active]: 'Active',
  [EngagementFeedBucket.Closing]: 'Closing',
  [EngagementFeedBucket.Closed]: 'Closed',
};

/** Display order, which is the lifecycle order and not the enum's alphabetical one. */
export const STAFF_BUCKETS: StaffFeedBucket[] = [
  EngagementFeedBucket.All,
  EngagementFeedBucket.Pending,
  EngagementFeedBucket.Active,
  EngagementFeedBucket.Closing,
  EngagementFeedBucket.Closed,
];

/**
 * The api's own enum, aliased rather than re-declared — same reason as the
 * bucket above, and here it is load-bearing rather than tidy: the wire values
 * are snake_case (`hours_escalated`), so a hand-written camelCase union
 * type-checks perfectly, matches nothing at runtime, and renders an empty
 * Red flags column on every row. Fixtures written to the same invented
 * vocabulary agree with it, so the tests pass too. Take the generated enum and
 * a mismatch is a compile error instead.
 */
export type StaffRedFlag = EngagementRedFlag;

/** Hover and screen-reader copy, keyed to the api's `EngagementRedFlag`. */
export const STAFF_RED_FLAG_LABELS: Record<StaffRedFlag, string> = {
  [EngagementRedFlag.HoursEscalated]: 'Hours escalated',
  [EngagementRedFlag.NteThreshold]: 'NTE near cap, no amendment',
  [EngagementRedFlag.GovernanceStale]: 'Governance pending over 3 days',
  [EngagementRedFlag.GhostExpert]: 'Expert silent past the ghost threshold',
  [EngagementRedFlag.BillingFailed]: 'Billing period failed to settle',
  [EngagementRedFlag.UnansweredOfChannel]: 'OF channel opened, nobody joined',
};

/**
 * The api's stable display order. Held here rather than trusting the order the
 * server happened to send, so two rows raising the same classes always draw
 * their dots in the same places.
 */
export const STAFF_RED_FLAG_ORDER: StaffRedFlag[] = [
  EngagementRedFlag.HoursEscalated,
  EngagementRedFlag.NteThreshold,
  EngagementRedFlag.GovernanceStale,
  EngagementRedFlag.GhostExpert,
  EngagementRedFlag.BillingFailed,
  EngagementRedFlag.UnansweredOfChannel,
];

export interface StaffFeedRow {
  engagementId: string;
  customerName: string;
  projectName: string;
  expertName: string;
  expertHasProfile: boolean;
  status: string;
  awaiting: string | null;
  hasOffer: boolean;
  nteConsumed: number | null;
  nteTotal: number | null;
  /** Whole percent, or null when there is no ceiling to be a percent of. */
  nteConsumedPct: number | null;
  lastActivityIso: string | null;
  flags: StaffRedFlag[];
  flagCount: number;
  billingFailureReason: string | null;
}

/** The placeholder these surfaces use for an absent value. */
const NONE = '—';

function toHours(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function fromStaffFeed(edges: readonly unknown[] | null | undefined): StaffFeedRow[] {
  return (edges ?? [])
    .map((edge) => (edge as { node?: Record<string, any> } | null)?.node)
    .filter((node): node is Record<string, any> => Boolean(node))
    .map((node): StaffFeedRow => {
      const total = toHours(node.nteHoursTotal);
      const consumed = toHours(node.nteHoursConsumed);
      // No ceiling means no offer yet, which is a different thing from a
      // ceiling of zero — the NTE cell must read "—", not "0h / 0h".
      const hasOffer = total !== null && total > 0;
      const flags: StaffRedFlag[] = node.redFlags?.flags ?? [];

      return {
        engagementId: String(node.id),
        // `group` is present only when the project hydrated. An un-hydrated row
        // is restricted, not broken, so it shows the placeholder rather than an
        // empty cell that reads as a missing customer.
        customerName: node.primaryProject?.group?.name ?? NONE,
        projectName: node.primaryProject?.name ?? node.primaryProjectName ?? NONE,
        expertName: node.expertIdentity?.name ?? 'Awaiting expert',
        expertHasProfile: Boolean(node.expertIdentity?.userId),
        status: node.status,
        awaiting: node.awaiting ?? null,
        hasOffer,
        nteConsumed: consumed,
        nteTotal: total,
        nteConsumedPct:
          hasOffer && consumed !== null ? Math.round((consumed / (total as number)) * 100) : null,
        // The expert's own last action, or nothing. NO FALLBACK, deliberately.
        //
        // The api returns null here when the expert has never acted, and says
        // why: "a rendered activation date is indistinguishable from a real
        // activity instant and would show a plausible timestamp where the truth
        // is silence since day one." `updatedAt` moves on any write by any
        // party or background job, so falling back to it reproduces exactly the
        // lie the api refused to tell — and it would do so most visibly on a
        // row flagged ghost_expert, printing "2 hours ago" beside a dot that
        // means the expert has been silent for weeks.
        lastActivityIso: node.expertLastActivityAt ?? null,
        flags: STAFF_RED_FLAG_ORDER.filter((flag) => flags.includes(flag)),
        flagCount: node.redFlags?.count ?? flags.length,
        billingFailureReason: node.redFlags?.billingFailureReason ?? null,
      };
    });
}
